import { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import { Avatar, Col, Dropdown, Menu, Row } from 'antd';
import { TbDropletBolt } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { BaseSkeleton, EmptyView, OurDocsLink, SmallTag, SmallTooltip } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType, ListType, ListViewColumnDataType } from '@/types';

interface AutopopulatedColumnsDropdownProps {
    children?: ReactNode;
    dataSources?: Pick<ConnectedAccount, 'id' | 'type'>[];
    listType?: ListType;
}

const AutopopulatedColumnsDropdown: FC<AutopopulatedColumnsDropdownProps> = ({
    children,
    dataSources = [],
    listType,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    const {
        integrationLabelToIconMap,
        listColumnDataTypeToIconMap,
        listColumnKeyToIconMap,
        listTypeToAutopopulatingColumnsMapMap,
    } = useMaps();

    const mapToRender = listType ? listTypeToAutopopulatingColumnsMapMap[listType] : undefined;
    const columnGroups = dataSources
        .map((dataSource) => ({
            dataSource,
            columns: (dataSource.type && mapToRender?.[dataSource.type]) || [],
        }))
        .filter(({ columns }) => columns.length > 0);
    const allColumnsLength = new Set(columnGroups.flatMap(({ columns }) => columns.map(({ key }) => key))).size;

    return (
        <Dropdown
            trigger={['hover']}
            placement={'rightBottom' as any}
            popupRender={() => (
                <DropdownMenuPanel
                    style={{ minWidth: 16 * 20, maxHeight: 16 * 24, overflowY: 'scroll', marginLeft: '1.75rem' }}
                >
                    <div style={{ padding: '0 0.75rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>{allColumnsLength} Columns</small>
                    </div>
                    {columnGroups.length > 0 && !loading ? (
                        columnGroups.map(({ dataSource: ds, columns }) => {
                            return (
                                <Fragment key={ds.id}>
                                    <Menu.Divider />
                                    <Row
                                        style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}
                                        align={'middle'}
                                        wrap={false}
                                    >
                                        <Col flex='none'>
                                            <Avatar
                                                size={16}
                                                shape='square'
                                                style={{ border: `1px solid ${token.colorBorder}` }}
                                                icon={
                                                    <img
                                                        alt={ds?.type}
                                                        src={
                                                            integrationLabelToIconMap[ds?.type as ConnectedAccountType]
                                                        }
                                                        style={{
                                                            ...imgLogoStyling,
                                                            transform: 'scale(0.75)',
                                                        }}
                                                    />
                                                }
                                            />
                                        </Col>
                                        <Col flex='none' style={{ paddingLeft: '0.375rem' }}>
                                            <small style={{ color: token.colorTextTertiary }}>
                                                <span>{ds?.type}</span>
                                            </small>
                                        </Col>
                                        <Col flex='auto' />
                                    </Row>
                                    <div style={{ padding: '0 0.75rem', overflowX: 'hidden' }}>
                                        {columns.map((lc) => {
                                            return (
                                                <Row
                                                    key={lc?.key}
                                                    align='middle'
                                                    wrap={false}
                                                    style={{ marginBottom: '0.125rem' }}
                                                    gutter={[4, 0]}
                                                >
                                                    <Col flex='none'>
                                                        <span style={{ color: token.colorTextTertiary }}>
                                                            <span
                                                                style={{
                                                                    verticalAlign: '-0.125rem',
                                                                    marginRight: '0.25rem',
                                                                }}
                                                            >
                                                                {listColumnKeyToIconMap[lc?.key]}
                                                            </span>
                                                        </span>
                                                        {lc?.label}
                                                    </Col>
                                                    {lc?.enriched && (
                                                        <Col flex='none'>
                                                            <SmallTooltip
                                                                placement='rightTop'
                                                                padding='0 0.25rem'
                                                                title={
                                                                    <span>
                                                                        This column may be enriched for an improved
                                                                        product experience. You can learn more about how
                                                                        we apply data enrichment in <OurDocsLink />
                                                                    </span>
                                                                }
                                                            >
                                                                <TbDropletBolt
                                                                    style={{
                                                                        color: token.purple6,
                                                                        verticalAlign: '-0.1rem',
                                                                    }}
                                                                />
                                                            </SmallTooltip>
                                                        </Col>
                                                    )}
                                                    <Col flex='none'></Col>
                                                    <Col flex='auto' />
                                                    <Col flex='none'>
                                                        <SmallTag
                                                            message={
                                                                <>
                                                                    <span
                                                                        style={{
                                                                            verticalAlign: '-0.125rem',
                                                                            marginRight: '0.125rem',
                                                                            color: token.colorTextTertiary,
                                                                        }}
                                                                    >
                                                                        {
                                                                            listColumnDataTypeToIconMap[
                                                                                lc?.dataType as ListViewColumnDataType
                                                                            ]
                                                                        }
                                                                    </span>
                                                                    <span style={{ color: token.colorTextTertiary }}>
                                                                        {lc?.dataType}
                                                                    </span>
                                                                </>
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            );
                                        })}
                                    </div>
                                </Fragment>
                            );
                        })
                    ) : loading ? (
                        <BaseSkeleton />
                    ) : (
                        <EmptyView
                            height='20rem'
                            icon={
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='75%'
                                    height='75%'
                                    style={{
                                        color: token.colorTextTertiary,
                                        transform: 'rotate(180deg)',
                                    }}
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-chart-column'
                                >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M4 20h3' />
                                    <path d='M17 20h3' />
                                    <path d='M10.5 20h3' />
                                    <path d='M4 16h3' />
                                    <path d='M17 16h3' />
                                    <path d='M10.5 16h3' />
                                    <path d='M4 12h3' />
                                    <path d='M17 12h3' />
                                    <path d='M10.5 12h3' />
                                    <path d='M4 8h3' />
                                    <path d='M17 8h3' />
                                    <path d='M4 4h3' />
                                </svg>
                            }
                            label='No Autopopulated Columns'
                            description={
                                dataSources.length > 0
                                    ? 'No default columns are available for the selected List Type and Data Sources. You can add columns after creating your List.'
                                    : 'Add a Data Source to see which columns will autopopulate to your new List.'
                            }
                        />
                    )}
                </DropdownMenuPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default AutopopulatedColumnsDropdown;
